import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonApiService } from '../../common-api.service';
import { SpeakerService } from '../../speaker.service';
import { schedulefilterParameters } from '../joborder/joborder.interface';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { ListStateService } from '../../list-state.service';
import { FilterBuilderComponent } from '../filter-builder/filter-builder.component';
import { RenderFilterDropdownService } from '../../render-filter-dropdown.service';
import { SnackbarService } from '../../snackbar.service';
import { Subject, takeUntil } from 'rxjs';


const displayColumns = ['job_type', 'procedure', 'job_no', 'job_date', 'doctor_name', 'patient_name', 'job_order_product_names', 'delivery_date', 'current_process_name', 'next_process_name', 'status'];
const displayHeaders = ['Job Type', 'Procedure', 'Job No.', 'Job Date', 'Doctor Name', 'Patient Name', 'Product Names', 'Delivery Date', 'Current Process', 'Next Process', 'Status'];


@Component({
	selector: 'app-job-schedule',
	templateUrl: './job-schedule.component.html',
	styleUrls: ['./job-schedule.component.css']
})
export class JobScheduleComponent implements OnInit, OnDestroy {

	pageIndex = environment.defaultPageIndexOnLoad;
	pageSize = environment.defaultPageSizeOnLoad;
	sortQuery = 'id:1';
	private destroy$ = new Subject<void>();
	filterCriteria: any[] = [];
	doctorOptions: any[] = [];
	categoryOptions: any[] = [];
	result: string;

	constructor(
		public speaker: SpeakerService,
		private api: CommonApiService,
		private router: Router,
		public dialog: MatDialog,
		private datePipe: DatePipe,
		private renderFilterDropdown: RenderFilterDropdownService,
		private snackbar: SnackbarService,
		private listState: ListStateService
	) { }

	ngOnInit(): void {
		this.speaker.model = 'jobschedule';
		this.speaker.displayColumns = displayColumns;
		this.speaker.displayHeaders = displayHeaders;
		this.speaker.showActionColumn = true;
		this.speaker.actionColumnLabel = 'Schedule';

		const cached = this.listState.restore(this.speaker.model);
		if (cached) {
			this.pageIndex = cached.pageIndex;
			this.pageSize = cached.pageSize;
			this.sortQuery = cached.sortQuery;
			this.filterCriteria = cached.filterCriteria;
			this.speaker.activeView = cached.activeView;
		}
		this.speaker.pageIndex = this.pageIndex;
		this.speaker.pageSize = this.pageSize;
		this.speaker.sortQuery = this.sortQuery;

		this.speaker.paginationChange$.pipe(takeUntil(this.destroy$)).subscribe(({ pageIndex, pageSize, sortQuery }) => {
			this.pageIndex = pageIndex;
			this.pageSize = pageSize;
			this.sortQuery = sortQuery;
			this.getDraftJobs();
		});
		this.speaker.activeView$.pipe(takeUntil(this.destroy$)).subscribe(() => this.getDraftJobs());
		this.speaker.actionClick$.pipe(takeUntil(this.destroy$)).subscribe((row) => {
			this.confirmDialog(row.id);
		});

		this.getDraftJobs();
		this.onDropdownRenderOptions('doctor/list/display', this.doctorOptions);
		this.onDropdownRenderOptions('category/list/display', this.categoryOptions);
	}

	ngOnDestroy(): void {
		this.speaker.showActionColumn = false;
		this.speaker.actionColumnLabel = '';
		this.destroy$.next();
		this.destroy$.complete();
	}

	onDropdownRenderOptions(apiEndpoint: string, targetOptions: any[]) {
		this.renderFilterDropdown.onDropdownLoadOptions(apiEndpoint, targetOptions);
	}

	confirmDialog(job_id: number): void {
		const message = `Are you sure you want to schedule this job for production?`;
		const dialogConfig = new MatDialogConfig();
		dialogConfig.data = {
			title: 'Confirm Job Schedule',
			message: message
		};
		const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
		dialogRef.afterClosed().subscribe(dialogResult => {
			this.result = dialogResult;
			if (this.result) {
				this.scheduleJobOrder(job_id);
			}
		});
	}

	scheduleJobOrder(job_id: number): any {
		this.api.callRoute('joborder', `/schedule/${job_id}`, null).subscribe(
			(resp) => {
				if (resp.status != 'error') {
					this.snackbar.success('Scheduled Successfully!');
					this.redirectTo('jobschedule');
				} else {
					this.snackbar.info(resp.message);
				}
			},
			error => {
				console.log(error);
			}
		);
	}

	getDraftJobs() {
		const params: any = {
			offset: this.pageIndex,
			limit: this.pageSize,
			sort: this.sortQuery,
			active: this.speaker.activeView,
			filterCriteria: this.filterCriteria
		};

		this.api.postAll('joborder/jobschedule', params).subscribe((res: any) => {
			const listData = res.items;
			listData.forEach((element: any) => {
				element.doctor_name = element.doctor?.name || '';
				element.job_order_product_names = element.products?.map((product: any) => product.name).join('<br>') || '';
				const inProd = element.status === 'in_production';
				element.current_process_name = inProd ? (element.current_process?.process?.name || '') : '';
				element.next_process_name = inProd ? (element.next_process?.process?.name || '') : '';
			});

			this.speaker.setListData(listData, res.rowCount, res.pageIndex, res.pageSize);
			this.listState.save(this.speaker.model, { pageIndex: this.pageIndex, pageSize: this.pageSize, sortQuery: this.sortQuery, filterCriteria: this.filterCriteria, activeView: this.speaker.activeView });
		});
	}

	transformDate(date: any) {
		date = new Date(date);
		return this.datePipe.transform(date, 'dd-MM-yyyy');
	}

	getOptionArray<T>(modelName: any): T[] {
		const ModelOptionsMap: Record<string, T[]> = {
			doctor: this.doctorOptions,
			category: this.categoryOptions,
		};
		return ModelOptionsMap[modelName];
	}

	openDialogFilter() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.panelClass = 'custom-dialog-container';
		dialogConfig.data = {
			filterFields: schedulefilterParameters.map(field => field),
		};
		const dialogRef = this.dialog.open(FilterBuilderComponent, dialogConfig);

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.filterCriteria = result;
				this.pageIndex = environment.defaultPageIndexOnLoad;
				this.pageSize = environment.defaultPageSizeOnLoad;
				this.getDraftJobs();
			}
		});
	}

	resetFilters() {
		this.listState.clear(this.speaker.model);
		this.filterCriteria = [];
		this.redirectTo('jobschedule');
	}

	redirectTo(uri: string) {
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
			this.router.navigate([uri]));
	}
}

